import { classNames } from "@/utils/classnames";
import { Icon, type IconSize } from "@/components/icons/Icon";
// eslint-disable-next-line no-restricted-imports -- ChalkLink is the sole runtime wrapper around Next.js Link.
import Link from "next/link";
import React, { forwardRef } from "react";

export type ChalkLinkProps = Omit<React.ComponentProps<typeof Link>, "target"> & {
  /** Use link semantics without the default inline-text palette. */
  unstyled?: boolean;
  /** Open the destination in a new tab with safe browser attributes and a visual indicator. */
  openInNewTab?: boolean;
  /** Composite navigation components can disable the indicator when they render their own. */
  showOpenInNewTabIcon?: boolean;
  /** Composite navigation can match the indicator to its surrounding controls. */
  openInNewTabIconSize?: IconSize;
};

const getNewTabRel = (rel?: string) =>
  Array.from(
    new Set([...(rel?.split(/\s+/).filter(Boolean) ?? []), "noopener", "noreferrer"]),
  ).join(" ");

/** The default inline link treatment for dashboard navigation. */
export const ChalkLink = forwardRef<HTMLAnchorElement, ChalkLinkProps>(function ChalkLink(
  {
    children,
    className,
    openInNewTab = false,
    rel,
    showOpenInNewTabIcon = true,
    openInNewTabIconSize = "text",
    unstyled = false,
    ...props
  },
  ref,
) {
  return (
    <Link
      ref={ref}
      className={classNames(
        !unstyled &&
          "cursor-pointer text-cds-link-text-default focus:outline-cds-bright-green-500 hover:text-cds-link-text-hover dark:text-cds-link-text-default-dark dark:hover:text-cds-link-text-hover-dark",
        className,
      )}
      {...props}
      target={openInNewTab ? "_blank" : undefined}
      rel={openInNewTab ? getNewTabRel(rel) : rel}
    >
      {props.legacyBehavior ? (
        children
      ) : (
        <>
          {children}
          {openInNewTab && showOpenInNewTabIcon && (
            <span className="ml-[0.25em] inline-flex h-[1lh] -translate-y-[0.1em] items-center align-middle">
              <Icon name="externalLink" size={openInNewTabIconSize} aria-hidden="true" />
            </span>
          )}
        </>
      )}
    </Link>
  );
});

ChalkLink.displayName = "ChalkLink";
